import { computed, inject } from '@angular/core';

import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';

import { ENTITY_ICONS } from '../config/entity-icons.config';
import { FeatureFlagKey } from '../models/feature-flags';
import { FeatureFlagService } from '../services/feature-flag/feature-flag.service';
import { AuthStore } from './auth.store';
import { FarmStore } from './farm.store';

export interface MenuItem {
  id?: number;
  displayName?: string;
  iconName?: string;
  route?: string;
  action?: 'openVocalAssistant'; // Action-based menu items
  bgcolor?: string;
  divider?: boolean;
  navCap?: string;
  requiresAdmin?: boolean;
  requiresFarmContext?: boolean;
  external?: boolean;
  twoLines?: boolean;
  disabled?: boolean;
  children?: MenuItem[];
  dataTour?: string; // data-tour attribute for guided tour targeting
  featureFlag?: FeatureFlagKey; // Feature flag to control visibility
}

export interface MenuSection {
  id: number;
  name: string;
  children: MenuItem[];
  requiresFarmContext?: boolean;
  featureFlag?: FeatureFlagKey; // Feature flag to control section visibility
}

interface MenuState {
  menuSections: MenuSection[];
  activeSectionId: number;
}

const initialState: MenuState = {
  menuSections: [],
  activeSectionId: 1,
};

const staticMenuData: MenuSection[] = [
  {
    id: 1,
    name: 'Main',
    children: [
      {
        navCap: ' ',
      },
      {
        displayName: 'menu.dashboard',
        iconName: ENTITY_ICONS.dashboard,
        bgcolor: 'primary',
        route: '/app/dashboard',
        dataTour: 'nav-dashboard',
      },
      {
        displayName: 'menu.vocalAssistant',
        iconName: ENTITY_ICONS.aiAssistant,
        bgcolor: 'ai-assistant-color',
        action: 'openVocalAssistant',
        dataTour: 'nav-ai',
      },
      {
        divider: true,
        navCap: 'menu.sections.farmOperations',
      },
      {
        displayName: 'menu.tasks',
        iconName: ENTITY_ICONS.tasks,
        bgcolor: 'tasks-color',
        route: '/app/tasks',
        requiresFarmContext: true,
        dataTour: 'nav-tasks',
      },
      {
        displayName: 'menu.fields',
        iconName: ENTITY_ICONS.fields,
        bgcolor: 'fields-color',
        route: '/app/fields',
        requiresFarmContext: true,
        dataTour: 'nav-fields',
      },
      {
        displayName: 'menu.crops',
        iconName: ENTITY_ICONS.crops,
        bgcolor: 'crops-color',
        route: '/app/crops/dashboard',
        requiresFarmContext: true,
        dataTour: 'nav-crops',
      },
      {
        displayName: 'menu.livestock',
        iconName: ENTITY_ICONS.livestock,
        bgcolor: 'livestock-color',
        route: '/app/livestock/dashboard',
        requiresFarmContext: true,
        dataTour: 'nav-livestock',
      },
      {
        displayName: 'menu.equipment',
        iconName: ENTITY_ICONS.equipment,
        bgcolor: 'equipment-color',
        route: '/app/equipment/tiles',
        requiresFarmContext: true,
        dataTour: 'nav-equipment',
      },
      {
        displayName: 'menu.buildings',
        iconName: ENTITY_ICONS.buildings,
        bgcolor: 'buildings-color',
        route: '/app/buildings',
        requiresFarmContext: true,
        dataTour: 'nav-buildings',
      },
      {
        displayName: 'menu.drafts',
        iconName: ENTITY_ICONS.drafts,
        bgcolor: 'drafts-color',
        route: '/app/drafts',
        requiresFarmContext: true,
        dataTour: 'nav-drafts',
      },
      {
        displayName: 'menu.team',
        iconName: ENTITY_ICONS.team,
        bgcolor: 'team-color',
        route: '/app/team/dashboard',
        requiresFarmContext: true,
        dataTour: 'nav-team',
      },
      {
        divider: true,
        navCap: 'menu.sections.accounting',
      },
      {
        displayName: 'menu.transactions',
        iconName: ENTITY_ICONS.accounting,
        bgcolor: 'accounting-color',
        route: '/app/transactions',
        requiresFarmContext: true,
        dataTour: 'nav-transactions',
      },
      {
        displayName: 'menu.reportsDashboard',
        iconName: ENTITY_ICONS.reports,
        bgcolor: 'accounting-color',
        route: '/app/reports/dashboard',
        requiresFarmContext: true,
        dataTour: 'nav-reports',
      },
      {
        displayName: 'menu.inventory',
        iconName: ENTITY_ICONS.inventory,
        bgcolor: 'inventory-color',
        route: '/app/inventory',
        requiresFarmContext: true,
        dataTour: 'nav-inventory',
      },
      {
        displayName: 'menu.accountingReview',
        iconName: 'solar:clipboard-check-line-duotone',
        bgcolor: 'accounting-color',
        route: '/app/accounting/review',
        requiresFarmContext: true,
        dataTour: 'nav-accounting-review',
      },
      {
        divider: true,
        navCap: 'menu.sections.general',
      },
      {
        displayName: 'menu.upcomingFeatures',
        iconName: ENTITY_ICONS.upcomingFeatures,
        bgcolor: 'warning',
        route: '/app/upcoming-features',
        dataTour: 'nav-upcoming-features',
      },
      {
        displayName: 'menu.settings',
        iconName: ENTITY_ICONS.settings,
        bgcolor: 'accent',
        route: '/app/settings',
        requiresFarmContext: false,
        dataTour: 'nav-settings',
      },
      {
        displayName: 'menu.activityTracking',
        iconName: ENTITY_ICONS.activityTracking,
        bgcolor: 'info',
        route: '/app/activity-tracking',
        requiresFarmContext: true,
        dataTour: 'nav-activity-tracking',
        featureFlag: 'ACTIVITY_TRACKING',
      },
    ],
  },
  {
    id: 2,
    name: 'UI Experiment',
    featureFlag: 'UI_EXPERIMENT',
    children: [
      {
        navCap: ' ',
      },
      {
        divider: true,
        navCap: 'menu.sections.uiExperiment',
      },
      {
        displayName: 'UI Gallery',
        iconName: 'solar:palette-line-duotone',
        bgcolor: 'info',
        route: '/app/ui-gallery',
        dataTour: 'nav-ui-gallery',
        featureFlag: 'UI_EXPERIMENT',
      },
      {
        displayName: 'Tour Demo',
        iconName: 'solar:route-bold-duotone',
        bgcolor: 'info',
        route: '/app/tour-demo',
        dataTour: 'nav-tour-demo',
        featureFlag: 'UI_EXPERIMENT',
      },
      {
        displayName: 'Detail Experiment',
        iconName: 'solar:user-id-line-duotone',
        bgcolor: 'info',
        route: '/app/detail-view-experiment',
        dataTour: 'nav-detail-experiment',
        featureFlag: 'UI_EXPERIMENT',
      },
      {
        displayName: 'Accounting Experiment',
        iconName: 'solar:test-tube-line-duotone',
        bgcolor: 'accounting-color',
        route: '/app/accounting',
        dataTour: 'nav-accounting',
        featureFlag: 'UI_EXPERIMENT',
      },
      {
        displayName: 'Financial Dashboard Experiment',
        iconName: 'solar:chart-2-line-duotone',
        bgcolor: 'accounting-color',
        route: '/app/financial-dashboard-experiment',
        dataTour: 'nav-financial-dashboard-experiment',
        featureFlag: 'UI_EXPERIMENT',
      },
      {
        displayName: 'Transaction Dashboard Experiment',
        iconName: 'solar:card-transfer-line-duotone',
        bgcolor: 'accounting-color',
        route: '/app/transaction-dashboard-experiment',
        dataTour: 'nav-transaction-dashboard-experiment',
        featureFlag: 'UI_EXPERIMENT',
      },
    ],
  },
];

export const MenuStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    authStore: inject(AuthStore),
    farmStore: inject(FarmStore),
    featureFlagService: inject(FeatureFlagService),
  })),
  withComputed((store) => ({
    activeSection: computed(() =>
      store.menuSections().find(section => section.id === store.activeSectionId())
    ),
    visibleMenuSections: computed(() => {
      const isAdmin = store.authStore.isAdmin();
      const hasFarmContext = store.farmStore.hasCurrentFarm();
      const activeSectionId = store.activeSectionId();
      const currentFarmId = store.farmStore.currentFarm()?.id;
      const featureFlagService = store.featureFlagService;

      return store.menuSections()
        // Filter out sections that require a disabled feature flag
        .filter(section => {
          if (section.featureFlag) {
            return featureFlagService.isEnabled(section.featureFlag);
          }
          return true;
        })
        .map(section => ({
          ...section,
          children: filterMenuItems(section.children, isAdmin, hasFarmContext, featureFlagService).map(item =>
            replaceRouteParams(item, currentFarmId)
          ),
        }))
        .filter(section => {
          return section.children.length > 0 || section.id === activeSectionId;
        });
    }),
  })),
  withMethods((store) => ({
    setActiveSection(sectionId: number) {
      patchState(store, { activeSectionId: sectionId });
    },

    loadMenuData() {
      patchState(store, { menuSections: staticMenuData });
    },
  }))
);

function filterMenuItems(
  items: MenuItem[],
  isAdmin: boolean,
  hasFarmContext: boolean,
  featureFlagService: FeatureFlagService
): MenuItem[] {
  if (!items) return [];

  const filteredItems: MenuItem[] = [];
  let hasVisibleItemsAfterDivider = false;

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];

    if (item.divider || item.navCap) {
      if (hasVisibleItemsAfterDivider) {
        filteredItems.unshift(item);
      }
      hasVisibleItemsAfterDivider = false;
    } else if (shouldShowMenuItem(item, isAdmin, hasFarmContext, featureFlagService)) {
      filteredItems.unshift(item);
      hasVisibleItemsAfterDivider = true;
    }
  }

  return filteredItems;
}

function shouldShowMenuItem(
  item: MenuItem,
  isAdmin: boolean,
  hasFarmContext: boolean,
  featureFlagService: FeatureFlagService
): boolean {
  if (!item) return true;

  if (item.requiresAdmin && !isAdmin) return false;

  if (item.requiresFarmContext && !hasFarmContext) return false;

  // Check feature flag if specified
  if (item.featureFlag && !featureFlagService.isEnabled(item.featureFlag)) {
    return false;
  }

  return true;
}

function replaceRouteParams(item: MenuItem, currentFarmId: string | undefined): MenuItem {
  if (!item.route) return item;

  let route = item.route;
  if (route.includes('{farmId}') && currentFarmId) {
    route = route.replace('{farmId}', currentFarmId);
  }

  return {
    ...item,
    route,
    children: item.children?.map(child => replaceRouteParams(child, currentFarmId))
  };
}
